import React, { useState } from 'react';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Checkbox, Heading, HStack, Input, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Table, Tbody, Td, Text, Th,
  Thead, Tr, VStack, useToast,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { User, UserRole } from '../types/api';
import { ROLES, ROLE_LABELS, apiErrorText } from '../utils/roles';

interface FormState {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: UserRole;
}
const EMPTY_FORM: FormState = { email: '', first_name: '', last_name: '', phone: '', role: 'viewer' };

const EventsModal: React.FC<{ user: User; onClose: () => void }> = ({ user, onClose }) => {
  const toast = useToast();
  const qc = useQueryClient();
  const eventsQ = useQuery({
    queryKey: ['events-all-for-assign'],
    queryFn: () => apiService.getEvents({ page: 1, page_size: 100 }),
  });
  const assignedQ = useQuery({ queryKey: ['user-events', user.id], queryFn: () => apiService.getUserEvents(user.id) });
  const assigned = new Set((assignedQ.data ?? []).map((e) => e.id));

  const toggle = useMutation({
    mutationFn: ({ eventId, on }: { eventId: number; on: boolean }) =>
      on ? apiService.assignUserToEvent(user.id, eventId) : apiService.unassignUserFromEvent(user.id, eventId),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['user-events', user.id] });
      qc.invalidateQueries({ queryKey: ['event-team', v.eventId] });
    },
    onError: (err) => toast({ title: apiErrorText(err), status: 'error', duration: 6000 }),
  });

  return (
    <Modal isOpen onClose={onClose} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent data-testid="events-modal">
        <ModalHeader>Événements de {user.full_name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {user.role === 'admin' && (
            <Text fontSize="sm" color="gray.600" mb={3}>Un administrateur a accès à tous les événements.</Text>
          )}
          <VStack align="stretch" spacing={2}>
            {(eventsQ.data?.items ?? []).map((ev) => (
              <Checkbox
                key={ev.id}
                isChecked={assigned.has(ev.id)}
                isDisabled={toggle.isPending}
                onChange={(e) => toggle.mutate({ eventId: ev.id, on: e.target.checked })}
                data-testid={`assign-event-${ev.id}`}
              >
                {ev.title}
              </Checkbox>
            ))}
          </VStack>
        </ModalBody>
        <ModalFooter><Button onClick={onClose}>Fermer</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/** "Comptes" (#70, admin only): create and manage accounts, reset passwords, assign events. */
const AccountsPage: React.FC = () => {
  const toast = useToast();
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => apiService.listUsers() });
  const [form, setForm] = useState<FormState | null>(null);
  const [editing, setEditing] = useState<User | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ who: string; password: string } | null>(null);
  const [eventsFor, setEventsFor] = useState<User | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] });
  const onError = (err: any) => toast({ title: apiErrorText(err), status: 'error', duration: 6000 });

  const save = useMutation({
    mutationFn: async (f: FormState) => {
      if (editing) {
        await apiService.updateUser(editing.id, {
          first_name: f.first_name, last_name: f.last_name, phone: f.phone || null, role: f.role,
        });
        return null;
      }
      return apiService.createUser({ ...f, phone: f.phone || undefined });
    },
    onSuccess: (created) => {
      setForm(null);
      setEditing(null);
      setFormError(null);
      refresh();
      if (created) setSecret({ who: created.email, password: created.temporary_password });
    },
    onError: (err) => setFormError(apiErrorText(err)),
  });
  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      active ? apiService.activateUser(id) : apiService.deactivateUser(id),
    onSuccess: refresh,
    onError,
  });
  const reset = useMutation({
    mutationFn: (u: User) => apiService.resetUserPassword(u.id).then((r) => ({ u, r })),
    onSuccess: ({ u, r }) => setSecret({ who: u.email, password: r.temporary_password }),
    onError,
  });

  const openCreate = () => { setEditing(null); setFormError(null); setForm(EMPTY_FORM); };
  const openEdit = (u: User) => {
    setEditing(u);
    setFormError(null);
    setForm({ email: u.email, first_name: u.first_name, last_name: u.last_name, phone: u.phone ?? '', role: u.role });
  };
  const field = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => (f ? { ...f, [k]: e.target.value } : f));

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">Comptes</Heading>
        <Button colorScheme="blue" onClick={openCreate} data-testid="accounts-create">Créer un compte</Button>
      </HStack>

      {secret && (
        <Alert status="warning" borderRadius="md" mb={4} data-testid="accounts-secret">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Mot de passe temporaire de {secret.who} : <b data-testid="accounts-secret-value">{secret.password}</b>.
            Il n'est affiché qu'une fois : transmettez-le à la personne.
            <Button size="xs" ml={3} onClick={() => setSecret(null)}>Masquer</Button>
          </AlertDescription>
        </Alert>
      )}

      <Table size="sm" data-testid="accounts-table">
        <Thead>
          <Tr><Th>Nom</Th><Th>E-mail</Th><Th>Rôle</Th><Th>Statut</Th><Th /></Tr>
        </Thead>
        <Tbody>
          {(usersQ.data ?? []).map((u) => (
            <Tr key={u.id} data-testid={`account-row-${u.id}`}>
              <Td>{u.full_name}</Td>
              <Td>{u.email}</Td>
              <Td>{ROLE_LABELS[u.role]}</Td>
              <Td>{u.is_active ? <Badge colorScheme="green">Actif</Badge> : <Badge>Désactivé</Badge>}</Td>
              <Td>
                <HStack spacing={1} wrap="wrap">
                  <Button size="xs" onClick={() => openEdit(u)}>Modifier</Button>
                  <Button size="xs" onClick={() => setEventsFor(u)}>Événements</Button>
                  <Button size="xs" onClick={() => {
                    if (window.confirm(`Réinitialiser le mot de passe de ${u.full_name} ? L'ancien ne fonctionnera plus.`)) reset.mutate(u);
                  }}>Réinitialiser le mot de passe</Button>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => {
                      if (u.is_active && !window.confirm(`Désactiver le compte de ${u.full_name} ? La personne ne pourra plus se connecter.`)) return;
                      setActive.mutate({ id: u.id, active: !u.is_active });
                    }}
                    data-testid={`account-toggle-${u.id}`}
                  >
                    {u.is_active ? 'Désactiver' : 'Réactiver'}
                  </Button>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {form && (
        <Modal isOpen onClose={() => setForm(null)}>
          <ModalOverlay />
          <ModalContent data-testid="accounts-form">
            <ModalHeader>{editing ? 'Modifier le compte' : 'Créer un compte'}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={3} align="stretch">
                {formError && (
                  <Alert status="error" borderRadius="md" data-testid="accounts-form-error">
                    <AlertIcon /><AlertDescription fontSize="sm">{formError}</AlertDescription>
                  </Alert>
                )}
                <Input placeholder="E-mail" value={form.email} onChange={field('email')} isDisabled={!!editing} data-testid="accounts-email" />
                <Input placeholder="Prénom" value={form.first_name} onChange={field('first_name')} data-testid="accounts-first-name" />
                <Input placeholder="Nom" value={form.last_name} onChange={field('last_name')} data-testid="accounts-last-name" />
                <Input placeholder="Téléphone" value={form.phone} onChange={field('phone')} data-testid="accounts-phone" />
                <Select value={form.role} onChange={field('role')} data-testid="accounts-role">
                  {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </Select>
                {!editing && (
                  <Text fontSize="xs" color="gray.500">Un mot de passe temporaire sera généré et affiché une fois.</Text>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button onClick={() => setForm(null)}>Annuler</Button>
              <Button
                colorScheme="blue"
                onClick={() => save.mutate(form)}
                isDisabled={save.isPending || !form.email || !form.first_name || !form.last_name}
                data-testid="accounts-save"
              >
                Enregistrer
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {eventsFor && <EventsModal user={eventsFor} onClose={() => setEventsFor(null)} />}
    </Box>
  );
};

export default AccountsPage;
